import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styles from '../../styles/formComponent/Selectbox.module.scss';

const Selectbox = ({
  label,
  name,
  value = '',
  onChange,
  onBlur,
  onFocus,
  placeholder,
  required = false,
  disabled = false,
  error,
  helperText,
  variant = 'outlined',
  size = 'medium',
  options = [],
  className = '',
  autoFocus = false,
  multiple = false,
  maxDisplayItems = 3,
  endIcon, // Icon to display at the end of the select
  onEndIconClick, // Click handler for the end icon
  readOnly = false,
  openUpward = false, // Open dropdown above the select field
  allowClear = true,
  disableClearable = false,
  dropdownDirection = 'auto',
  ...props
}) => {
  const [focused, setFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [hasValue, setHasValue] = useState(multiple ? Array.isArray(value) && value.length > 0 : Boolean(value));
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    triggerTop: 0,
    triggerBottom: 0,
  });
  const selectRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setHasValue(multiple ? Array.isArray(value) && value.length > 0 : Boolean(value));
  }, [value, multiple]);

  useEffect(() => {
    if (autoFocus && selectRef.current) {
      selectRef.current.focus();
    }
  }, [autoFocus]);


  const handleFocus = (e) => {
    if (!disabled && !readOnly) {
      setFocused(true);
      if (onFocus) {
        onFocus(e);
      }
    }
  };

  const handleBlur = (e) => {
    if (onBlur) {
      onBlur(e);
    }
  };

  const calculateDropdownPosition = useCallback(() => {
    if (selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

      const estimatedOptionRows = Math.max(options.length, 1);
      const dropdownHeight = Math.min(250, estimatedOptionRows * 40);

      let top;
      if (openUpward) {
        top = rect.top + scrollTop - dropdownHeight - 2;
      } else {
        top = rect.bottom + scrollTop + 2;
      }

      return {
        top,
        left: rect.left + scrollLeft,
        width: rect.width,
        triggerTop: rect.top + scrollTop,
        triggerBottom: rect.bottom + scrollTop,
      };
    }
    return { top: 0, left: 0, width: 0 };
  }, [openUpward, options.length]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectRef.current && 
        !selectRef.current.contains(event.target) &&
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setFocused(false);
      }
    };

    const handleScroll = (event) => {
      if (isOpen) {
        // Don't reposition if scrolling within the dropdown itself
        if (dropdownRef.current && (
          dropdownRef.current.contains(event.target) ||
          event.target === dropdownRef.current
        )) {
          return;
        }
        
        // Only reposition if the scroll affects the select field's position
        requestAnimationFrame(() => {
          setDropdownPosition(calculateDropdownPosition());
        });
      }
    };

    const handleResize = () => {
      if (isOpen) {
        setDropdownPosition(calculateDropdownPosition());
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, calculateDropdownPosition]);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !readOnly) {
      if (!isOpen) {
        setDropdownPosition(calculateDropdownPosition());
      }
      setIsOpen(!isOpen);
      setFocused(true);
    }
  };

  const handleClear = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!allowClear || disableClearable || disabled || readOnly) return;

    setHasValue(false);
    setIsOpen(false);
    setFocused(false);

    // Blur the select so it returns to the resting state (label drops back down)
    selectRef.current?.blur();

    const event = {
      target: {
        name,
        value: multiple ? [] : ''
      }
    };
    if (onChange) {
      onChange(event);
    }
  };

  const handleOptionClick = (option, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    if (!option.disabled) {
      if (multiple) {
        const currentValues = Array.isArray(value) ? value : [];
        const isSelected = currentValues.includes(option.value);
        let newValues;
        
        if (isSelected) {
          newValues = currentValues.filter(v => v !== option.value);
        } else {
          newValues = [...currentValues, option.value];
        }
        
        const event = {
          target: {
            name,
            value: newValues
          }
        };
        
        setHasValue(newValues.length > 0);
        
        if (onChange) {
          onChange(event);
        }
      } else {
        const event = {
          target: {
            name,
            value: option.value
          }
        };
        
        setHasValue(Boolean(option.value));
        setIsOpen(false);
        setFocused(false);
        
        if (onChange) {
          onChange(event);
        }
      }
    }
  };

  const handleKeyDown = (e) => {
    if (disabled || readOnly) return;
    
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setFocused(false);
    } else if (isOpen && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      // Handle arrow key navigation within dropdown
      const currentIndex = options.findIndex(option => option.value === value);
      let newIndex;
      
      if (e.key === 'ArrowDown') {
        newIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
      } else {
        newIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
      }
      
      const newOption = options[newIndex];
      if (newOption && !newOption.disabled) {
        const event = {
          target: {
            name,
            value: newOption.value
          }
        };
        
        setHasValue(Boolean(newOption.value));
        if (onChange) {
          onChange(event);
        }
      }
    }
  };

  const getSelectedOptions = () => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      return options.filter(option => currentValues.includes(option.value));
    } else {
      return options.find(option => option.value === value) ? [options.find(option => option.value === value)] : [];
    }
  };

  const getDisplayValue = () => {
    const selectedOptions = getSelectedOptions();
    
    if (multiple) {
      if (selectedOptions.length === 0) {
        return placeholder && !focused ? placeholder : '';
      }
      
      if (selectedOptions.length <= maxDisplayItems) {
        return selectedOptions.map(option => option.label).join(', ');
      } else {
        const displayedLabels = selectedOptions.slice(0, maxDisplayItems).map(option => option.label);
        const remainingCount = selectedOptions.length - maxDisplayItems;
        return `${displayedLabels.join(', ')} +${remainingCount} more`;
      }
    } else {
      if (selectedOptions.length > 0) {
        return selectedOptions[0].label;
      }
      return placeholder && !focused ? placeholder : '';
    }
  };

  const isOptionSelected = (option) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      return currentValues.includes(option.value);
    } else {
      return option.value === value;
    }
  };

  const getSelectClasses = () => {
    let classes = styles.selectField;
    
    if (error) classes += ` ${styles.error}`;
    if (hasValue) classes += ` ${styles.filled}`;
    if (label) classes += ` ${styles.hasLabel}`;
    if (disabled) classes += ` ${styles.disabled}`;
    if (readOnly) classes += ` ${styles.readOnly}`;
    
    return classes;
  };

  const getLabelClasses = () => {
    let classes = styles.floatingLabel;
    
    // Add filled class when there's a value OR when focused (regardless of disabled/readOnly state)
    if (hasValue || (!disabled && !readOnly && focused)) classes += ` ${styles.filled}`;
    if (focused && !disabled && !readOnly) classes += ` ${styles.focused}`;
    if (error) classes += ` ${styles.error}`;
    if (disabled) classes += ` ${styles.disabled}`;
    if (readOnly) classes += ` ${styles.readOnly}`;
    
    return classes;
  };

  const showClearButton = !endIcon && allowClear && !disableClearable && hasValue && !disabled && !readOnly;

  const displayValueText = getDisplayValue() || (placeholder || '\u00A0');
  const displayValueTitle =
    !multiple &&
    hasValue &&
    typeof displayValueText === 'string' &&
    displayValueText.trim() &&
    displayValueText !== '\u00A0'
      ? displayValueText
      : undefined;

  const getWrapperClasses = () => {
    let classes = `${styles.selectWrapper}`;
    if (variant === 'filled') classes += ` ${styles.filled}`;
    if (variant === 'outlined') classes += ` ${styles.outlined}`;
    if (size === 'small') classes += ` ${styles.small}`;
    if (className) classes += ` ${className}`;
    if (endIcon) classes += ` ${styles.hasEndIcon}`;
    if (showClearButton) classes += ` ${styles.hasClearButton}`;
    if (readOnly) classes += ` ${styles.readOnly}`;
    
    return classes;
  };

  const getDropdownIconClasses = () => {
    let classes = styles.dropdownIcon;
    if (isOpen) classes += ` ${styles.open}`;
    if (disabled) classes += ` ${styles.disabled}`;
    if (readOnly) classes += ` ${styles.readOnly}`;
    return classes;
  };

  const renderEndIcon = () => {
    if (!endIcon) return null;
    
    const handleIconClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (onEndIconClick && !disabled && !readOnly) {
        onEndIconClick(e);
      }
    };

    return (
      <div 
        className={`${styles.endIcon} ${onEndIconClick && !disabled && !readOnly ? styles.clickable : ''}`}
        onClick={handleIconClick}
        style={{ pointerEvents: disabled || readOnly ? 'none' : 'auto' }}
      >
        {typeof endIcon === 'string' ? (
          <span>{endIcon}</span>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {endIcon}
          </div>
        )}
      </div>
    );
  };

  const DropdownPortal = () => {
    if (!isOpen) return null;

    const handleDropdownScroll = (e) => {
      // Prevent event bubbling to avoid repositioning
      e.stopPropagation();
    };

    // Calculate max height to prevent dropdown from extending beyond viewport
    const viewportHeight = window.innerHeight;
    const maxHeightDown = Math.min(250, viewportHeight - dropdownPosition.top - 20);
    const maxHeightUp = Math.min(250, Math.max(120, dropdownPosition.triggerTop - 20));
    const shouldAutoOpenUpward =
      dropdownDirection === 'auto' && maxHeightDown < 180 && maxHeightUp > maxHeightDown;
    const opensUpward = dropdownDirection === 'up' || shouldAutoOpenUpward;
    const resolvedMaxHeight = opensUpward ? maxHeightUp : maxHeightDown;
    const resolvedTop = opensUpward
      ? dropdownPosition.triggerTop - 2
      : dropdownPosition.top;

    const dropdownContent = (
      <div 
        ref={dropdownRef}
        className={`${styles.dropdownPortal} ${styles.dropdown}`}
        style={{
          position: 'absolute',
          top: resolvedTop,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          maxHeight: `${resolvedMaxHeight}px`,
          overflowY: 'auto',
          overflowX: 'hidden',
          zIndex: 9998,
          transform: opensUpward ? 'translateY(-100%)' : 'none'
        }}
        onScroll={handleDropdownScroll}
      >
        {options.length > 0 ? (
          options.map((option, index) => {
            const optionTitle =
              typeof option.title === 'string' && option.title.trim()
                ? option.title
                : typeof option.label === 'string'
                  ? option.label
                  : undefined;
            return (
            <div
              key={option.value || index}
              className={`
                ${styles.option} 
                ${isOptionSelected(option) ? styles.selected : ''} 
                ${option.disabled ? styles.disabled : ''}
                ${multiple ? styles.multiselectOption : ''}
              `}
              onClick={(e) => handleOptionClick(option, e)}
              role="option"
              aria-selected={isOptionSelected(option)}
              title={optionTitle}
            >
              {multiple && (
                <div className={styles.checkboxContainer}>
                  <input
                    type="checkbox"
                    checked={isOptionSelected(option)}
                    onChange={() => {}} // Handled by onClick
                    className={styles.checkbox}
                    disabled={option.disabled}
                  />
                </div>
              )}
              <span className={styles.optionLabel}>
                {option.label}
              </span>
              {Array.isArray(option.chips) && option.chips.length > 0 && (
                <span className={styles.optionChips}>
                  {option.chips.map((chip, chipIndex) => (
                    <span
                      key={`${option.value || index}-chip-${chipIndex}`}
                      className={`${styles.optionChip} ${
                        String(chip).toLowerCase() === "default"
                          ? styles.optionChipDefault
                          : styles.optionChipRecommended
                      }`}
                    >
                      {chip}
                    </span>
                  ))}
                </span>
              )}
            </div>
            );
          })
        ) : (
          <div className={styles.noOptions}>No options available</div>
        )}
      </div>
    );

    return createPortal(dropdownContent, document.body);
  };

  return (
    <>
      <div className={getWrapperClasses()}>
        <div className={styles.inputContainer}>
          <div
            ref={selectRef}
            className={getSelectClasses()}
            onClick={handleClick}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            tabIndex={disabled ? -1 : 0}
            role="combobox"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-required={required}
            aria-invalid={!!error}
            aria-readonly={readOnly}
            aria-disabled={disabled || readOnly}
            {...props}
          >
            <span
              className={`${!hasValue && !focused ? styles.placeholder : ''} ${multiple ? styles.multiselectDisplay : styles.singleSelectDisplay}`}
              title={displayValueTitle}
            >
              {displayValueText}
            </span>
          </div>
          
          {label && (
            <label className={getLabelClasses()}>
              {label}
              {required && <span style={{ color: '#dc2626' }}> *</span>}
            </label>
          )}
          
          {showClearButton && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={handleClear}
              tabIndex={-1}
              aria-label="Clear selection"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
          <div className={getDropdownIconClasses()}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 10L12 15L17 10H7Z"
                fill="currentColor"
              />
            </svg>
          </div>
          {renderEndIcon()}
        </div>
        {error && (
          <div className={styles.errorMessage}>{error}</div>
        )}
        {helperText && !error && (
          <div className={styles.helperText}>{helperText}</div>
        )}
      </div>
      
      <DropdownPortal />
    </>
  );
};

export default Selectbox; 